// The rules of a workflow on the number of actors of a level, shared by the back (the levels
// written or inheriting a change) and the front (the actors step). An id is compared by its
// string (an ObjectId in the back, a string in the front).

export type ActorListKey = 'internalActors' | 'externalActors';

export const BOTH_ACTOR_LISTS: readonly ActorListKey[] = [
  'internalActors',
  'externalActors',
];

export interface CountedActor {
  readonly kind: string;
  readonly categoryIds: readonly unknown[];
  readonly useItsEdmDomain?: boolean | null;
}

// The effective lists of a level.
export type EffectiveActorLists<A extends CountedActor = CountedActor> =
  Readonly<Record<ActorListKey, readonly A[]>>;

export interface CardinalityReferences {
  readonly organizationKind: string;
  readonly quotationEmitterCategoryId: unknown;
  readonly quotationReceiverCategoryId: unknown;
}

export interface CardinalityFlags {
  readonly atLeastOneInternal?: boolean | null;
  readonly atLeastOneExternal?: boolean | null;
  readonly atMostOneInternal?: boolean | null;
  readonly oneAndOnlyOneQuotationEmitter?: boolean | null;
  readonly oneAndOnlyOneQuotationReceiver?: boolean | null;
  readonly oneAndOnlyOneEdmDomain?: boolean | null;
}

export interface CardinalityRule {
  readonly code: keyof CardinalityFlags;
  // The list its actors belong to, the one to change to meet it.
  readonly list: ActorListKey;
  readonly isCounted: (actor: CountedActor, list: ActorListKey) => boolean;
  readonly isMet: (count: number) => boolean;
  readonly refusal: Readonly<{ en: string; fr: string }>;
}

const isAtLeastOne = (count: number) => count >= 1;
const isOneAndOnlyOne = (count: number) => count === 1;

const isSameId = (first: unknown, second: unknown) =>
  String(first) === String(second);

const hasCategory = (categoryId: unknown) => (actor: CountedActor) =>
  actor.categoryIds.some((id) => isSameId(id, categoryId));

export const cardinalityRulesOf = (
  rules: Readonly<CardinalityFlags>,
  references: Readonly<CardinalityReferences>,
): readonly CardinalityRule[] => {
  const candidates: readonly (CardinalityRule | false | null | undefined)[] = [
    rules.atLeastOneInternal && {
      code: 'atLeastOneInternal',
      list: 'internalActors',
      isCounted: (_: CountedActor, list: ActorListKey) =>
        list === 'internalActors',
      isMet: isAtLeastOne,
      refusal: {
        en: 'At least one internal actor is required',
        fr: 'Au moins un acteur interne est requis.',
      },
    },
    rules.atLeastOneExternal && {
      code: 'atLeastOneExternal',
      list: 'externalActors',
      isCounted: (_: CountedActor, list: ActorListKey) =>
        list === 'externalActors',
      isMet: isAtLeastOne,
      refusal: {
        en: 'At least one external actor is required',
        fr: 'Au moins un acteur externe est requis.',
      },
    },
    rules.atMostOneInternal && {
      code: 'atMostOneInternal',
      list: 'internalActors',
      isCounted: (_: CountedActor, list: ActorListKey) =>
        list === 'internalActors',
      isMet: (count: number) => count <= 1,
      refusal: {
        en: 'At most one internal actor: choose it among the internal actors of the parent level',
        fr: 'Un seul acteur interne : choisissez-le parmi les acteurs internes du niveau parent, qui en compte plusieurs.',
      },
    },
    rules.oneAndOnlyOneQuotationReceiver && {
      code: 'oneAndOnlyOneQuotationReceiver',
      list: 'externalActors',
      isCounted: hasCategory(references.quotationReceiverCategoryId),
      isMet: isOneAndOnlyOne,
      refusal: {
        en: 'One and only one "Quotation Receiver" actor is required',
        fr: 'Un et un seul acteur "Destinataire du Devis" est requis.',
      },
    },
    rules.oneAndOnlyOneQuotationEmitter && {
      code: 'oneAndOnlyOneQuotationEmitter',
      list: 'internalActors',
      isCounted: hasCategory(references.quotationEmitterCategoryId),
      isMet: isOneAndOnlyOne,
      refusal: {
        en: 'One and only one "Quotation Emitter" actor is required',
        fr: 'Un et un seul acteur "Émetteur du Devis" est requis.',
      },
    },
    rules.oneAndOnlyOneEdmDomain && {
      code: 'oneAndOnlyOneEdmDomain',
      list: 'internalActors',
      isCounted: (actor: CountedActor, list: ActorListKey) =>
        list === 'internalActors' &&
        actor.kind === references.organizationKind &&
        !!actor.useItsEdmDomain,
      isMet: isOneAndOnlyOne,
      refusal: {
        en: 'One and only one EDM domain is required',
        fr: 'Un et un seul domaine GED est requis.',
      },
    },
  ];

  return candidates.filter((rule): rule is CardinalityRule => !!rule);
};

const countIn = (
  rule: CardinalityRule,
  lists: EffectiveActorLists,
  list: ActorListKey,
) => lists[list].filter((actor) => rule.isCounted(actor, list)).length;

const totalCountOf = (rule: CardinalityRule, lists: EffectiveActorLists) =>
  BOTH_ACTOR_LISTS.reduce(
    (total, list) => total + countIn(rule, lists, list),
    0,
  );

export const brokenCardinalityRulesOf = (
  lists: EffectiveActorLists,
  rules: readonly CardinalityRule[],
): readonly CardinalityRule[] =>
  rules.filter((rule) => !rule.isMet(totalCountOf(rule, lists)));

export const brokenCardinalityRuleOf = (
  lists: EffectiveActorLists,
  rules: readonly CardinalityRule[],
): CardinalityRule | undefined => brokenCardinalityRulesOf(lists, rules)[0];

// A level inheriting changed lists keeps the former ones of its own when the change breaks a rule
// on the number of its actors it met: the lists it inherits whose count of that rule changes.
export const listsToFreeze = ({
  before,
  after,
  inherited,
  rules,
}: Readonly<{
  before: EffectiveActorLists;
  after: EffectiveActorLists;
  inherited: readonly ActorListKey[];
  rules: readonly CardinalityRule[];
}>): readonly ActorListKey[] => {
  const brokenByTheChange = rules.filter(
    (rule) =>
      rule.isMet(totalCountOf(rule, before)) &&
      !rule.isMet(totalCountOf(rule, after)),
  );

  return inherited.filter((list) =>
    brokenByTheChange.some(
      (rule) => countIn(rule, before, list) !== countIn(rule, after, list),
    ),
  );
};
